<script>
  import { ROUTES } from '../../routing/routes'
  import { getMarketModelingDefaultSubroute } from 'features/market-modeling/utils/marketModelingViewConfig'
  import { ROLES } from 'utils/constants'
  import logo from 'assets/imgs/logo-stacked.svg'
  import { currentPath } from 'utils/device'
  import { useLocation, navigate } from 'svelte5-router'
  import UserDropdown from './UserDropdown.svelte'
  import { user, emulateUser } from 'stores/auth'
  import EmulateActions from './EmulateActions.svelte'
  import ContactActions from './ContactActions.svelte'
  import { tabsElements, tabElementsAdmin, findNavbarInfoByUrl, isTabElementEnabled } from 'app/routing/navbarUtils'
  import { appSelected, tabSelected, navHeight, tilesInfo } from 'stores/navigation'
  import { DASHBOARD_ROUTES } from 'app/routing/routesLinks'
  import Drawer from '../../../shared/layout/Drawer/Drawer.svelte'

  let showNavButtons = $state(false)
  let isAdmin = $state(false)

  // get the current location and u
  const location = useLocation()
  const pathname = $derived($location.pathname)

  $effect(() => {
    currentPath.set(pathname)
    const pathInfo = findNavbarInfoByUrl(pathname)
    isAdmin = pathInfo?.fullType === 'Administrator' || false
    showNavButtons = pathInfo?.showNavbarButtons ?? false

    if (pathInfo) {
      appSelected.set(pathInfo.application)
      tabSelected.set(pathInfo.title)
    }
  })

  $effect(() => {
    navHeight.set($appSelected && showNavButtons ? 80 + 51 : 80)
  })

  let emulateUserObject = $derived(
    $emulateUser ? { name: `${$emulateUser?.firstName} ${$emulateUser?.lastName}`, email: $emulateUser?.email, picture: null, nickname: null } : null
  )

  function getPersonaSwitchUrl(targetPersona) {
    if (!$location?.pathname) {
      return null
    }

    const currentPath = $location.pathname

    if (currentPath.startsWith('/app/market-modeling') || currentPath.startsWith('/phy/market-modeling')) {
      const targetPrefix = targetPersona === 'APP' ? '/app' : '/phy'
      const defaultSubroute = getMarketModelingDefaultSubroute(targetPersona)

      return `${targetPrefix}/market-modeling/${defaultSubroute}`
    }

    if (targetPersona === 'Phy' && currentPath.includes('/app')) {
      return currentPath.replace('/app', '/phy')
    }

    if (targetPersona === 'APP' && currentPath.includes('/phy')) {
      return currentPath.replace('/phy', '/app')
    }

    return null
  }

  function gotToPhysician() {
    appSelected.set('Phy')
    const newUrl = getPersonaSwitchUrl('Phy')
    if (newUrl) {
      navigate(newUrl)
    }
  }

  function gotToAdvancePracticeProvider() {
    appSelected.set('APP')
    const newUrl = getPersonaSwitchUrl('APP')
    if (newUrl) {
      navigate(newUrl)
    }
  }

  let canEmulate = $derived(
    $emulateUser || ($user && $user.cn_roles && ($user.cn_roles.includes(ROLES.APPLICATION_USER) || $user.cn_roles.includes(ROLES.APPLICATION_ADMIN)))
  )
  let hasAdminRole = $derived(Boolean($user?.cn_roles?.includes(ROLES.APPLICATION_ADMIN)))
  let tabItems = $derived(isAdmin && hasAdminRole ? tabElementsAdmin : tabsElements)

  function tabClick(item) {
    if (item.name !== $tabSelected) {
      tabSelected.set(item.name)
      let url
      if (isAdmin) {
        url = DASHBOARD_ROUTES.admin[item.appCode]?.url
      } else if ($appSelected === 'Phy') {
        url = DASHBOARD_ROUTES.physicianDashboards[item.phyCode].url
      } else {
        url = DASHBOARD_ROUTES.appDashboards[item.appCode].url
      }
      if (url) {
        navigate(url)
      }
    }
  }
</script>

<nav class="fixed top-0 z-[70] h-[{$navHeight}px] w-full bg-white" data-cy="landing-navbar">
  <div class="px-3 py-3 lg:px-5 lg:pl-3">
    <div class="flex items-start justify-between">
      <div class="flex items-center justify-start rtl:justify-end">
        <a href={ROUTES.homeDashboard} data-cy="logo-sullivan" class="-mt-1 md:mr-10 cursor-pointer">
          <img alt="Sullivan Logo" src={logo} />
        </a>
        <a href={ROUTES.clientPortalUrl} class="nav-button white-button" data-cy="client-portal-link" target="_blank">Client Portal</a>
        {#if showNavButtons && !isAdmin}
          <a href={ROUTES.homeDashboard} class="nav-button white-button" data-cy="home-link">Home</a>
          <button
            type="button"
            class="nav-button primary-button {$appSelected === 'Phy' ? 'selected' : ''}"
            data-cy="nav-physician-button"
            onclick={gotToPhysician}
            >Physician
          </button>

          <button
            type="button"
            class="nav-button secondary-button {$appSelected === 'APP' ? 'selected' : ''} "
            data-cy="nav-app-button"
            onclick={gotToAdvancePracticeProvider}
            >Advanced Practice Provider
          </button>
        {/if}
      </div>

      <div class="flex items-center" data-cy="client-portal-container">
        <div class="flex items-start ms-3">
          {#if canEmulate}
            <EmulateActions />
          {/if}
          {#if !isAdmin}
            <ContactActions />
          {/if}
          {#if $emulateUser}
            <UserDropdown user={emulateUserObject} />
          {:else}
            <UserDropdown user={$user} {hasAdminRole} />
          {/if}
        </div>
      </div>
    </div>
  </div>
  {#if $appSelected && showNavButtons}
    <div class="tab {isAdmin ? 'admin' : $appSelected === 'APP' ? 'app' : ''}" data-cy="nav-tab-buttons">
      {#each tabItems as item, i (item.name)}
        {#if isTabElementEnabled($tilesInfo, $appSelected === 'APP' ? item.appCode : item.phyCode)}
          <button
            class="tab-button {item.name === $tabSelected ? 'selected' : ''}"
            data-cy="tab-button-{i}"
            onclick={() => {
              tabClick(item)
            }}
          >
            <img alt={item.name} class="icon {item.name === $tabSelected ? 'hidden' : ''}" src={item.icon} />
            <img alt={item.name} class="icon {item.name !== $tabSelected ? 'hidden' : ''}" src={item.iconSelected} />
            <span>{item.name}</span>
          </button>
          {#if i < tabItems.length - 1 && item.name !== $tabSelected}
            <span class="separator"></span>
          {/if}
        {/if}
      {/each}
    </div>
  {/if}
  <Drawer />
</nav>
